import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Alert } from '@/design-system/alert';
import { Button } from '@/design-system/button';
import { TableData, type TableDataColumn } from '@/design-system/table-data';
import { toast } from '@/design-system/toaster';

import { parseWarehouseError, warehouseApi } from '../../api/warehouse.api';
import { ActionDialog, ShortfallModal } from '../../components/dialogs';
import { FormField, TextArea, TextInput } from '../../components/form';
import { HubSelect } from '../../components/pickers';
import { StockBatchSelect } from '../../components/StockBatchSelect';
import { StorageSelect } from '../../components/StorageSelect';
import {
  codeCls,
  DetailHeader,
  HubRequired,
  KV,
  KVGrid,
  metaCls,
  nameCls,
  numCls,
  Section,
  StockTypeBadge,
} from '../../components/ui';
import { WizardFooter, WizardSteps } from '../../components/wizard';
import { fmtDate, fmtQty, isExpired } from '../../format';
import { useServerErrors } from '../../hooks/useServerErrors';
import { storagePathOf, useStorageLabels } from '../../hooks/useStorages';
import { useWizard } from '../../hooks/useWizard';
import { WAREHOUSE_PATHS } from '../../paths';
import { STOCK_TYPE_META } from '../../status';
import type {
  FieldError,
  ShortfallRow,
  TransferLine,
  WarehouseStorage,
} from '../../types';
import { useWarehouse } from '../../WarehouseContext';
import {
  checkInfo,
  checkLines,
  type DraftLine,
  newLine,
  qtyIssue,
  reviewLines,
  SAME_STORAGE,
  toPayload,
  totalQty,
  TRANSFER_CODE_FIELD,
  transferFieldOf,
  transferStepOf,
} from './transferDraft';

const STEPS = [
  { id: 1, label: 'Informasi' },
  { id: 2, label: 'Daftar produk' },
  { id: 3, label: 'Review' },
];

const NO_STOCK_IDS: ReadonlySet<string> = new Set();

const REVIEW_COLUMNS: TableDataColumn<TransferLine>[] = [
  {
    key: 'sku',
    title: 'SKU',
    render: (_v, l) => <span className={codeCls}>{l.sku}</span>,
  },
  {
    key: 'itemName',
    title: 'Nama produk',
    render: (_v, l) => <span className={nameCls}>{l.itemName}</span>,
  },
  {
    key: 'batchNumber',
    title: 'Batch',
    render: (_v, l) => <span className={numCls}>{l.batchNumber}</span>,
  },
  {
    key: 'stockType',
    title: 'Tipe',
    render: (_v, l) => <StockTypeBadge type={l.stockType} />,
  },
  {
    key: 'expiredDate',
    title: 'Expiry',
    render: (_v, l) =>
      l.expiredDate ? (
        <span
          className={`${metaCls} ${isExpired(l.expiredDate) ? 'text-(--state-error-base)' : ''}`}
        >
          {fmtDate(l.expiredDate)}
          {isExpired(l.expiredDate) ? ' · expired' : ''}
        </span>
      ) : (
        <span className={metaCls}>Tanpa expiry</span>
      ),
  },
  {
    key: 'qty',
    title: 'Qty',
    align: 'right',
    render: (_v, l) => <span className={numCls}>{fmtQty(l.qty)}</span>,
  },
  {
    key: 'uomName',
    title: 'UoM',
    render: (_v, l) => <span className={metaCls}>{l.uomName}</span>,
  },
];

const InlineError: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mt-1 text-[11px] text-(--state-error-base)" role="alert">
    {children}
  </p>
);

const LINE_GRID =
  'grid grid-cols-[minmax(0,1fr)_112px_48px_40px] items-start gap-2';

/* --------------------------------- form ---------------------------------- */

interface TransferFormProps {
  hubID: number;
  hubName: string | undefined;
}

const TransferForm: React.FC<TransferFormProps> = ({ hubID, hubName }) => {
  const navigate = useNavigate();
  const [sourceID, setSourceID] = useState('');
  const [destID, setDestID] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DraftLine[]>(() => [newLine()]);
  /** A source storage the user picked while rows exist: waits for confirmation. */
  const [pendingSource, setPendingSource] = useState<WarehouseStorage | null>(
    null
  );
  const [busy, setBusy] = useState(false);
  const [shortfall, setShortfall] = useState<{
    rows: ShortfallRow[];
    message: string;
  } | null>(null);
  const server = useServerErrors(transferFieldOf, transferStepOf);
  const { errors, setErrors, fieldError: fe, lineError } = server;

  // One `ids=` request for both bins' labels (the pickers remember what they pick).
  const labels = useStorageLabels(hubID, [sourceID, destID]);
  const sourceStorage = labels.byID.get(sourceID);
  const destStorage = labels.byID.get(destID);
  const storageText = (s: WarehouseStorage | undefined) =>
    s ? storagePathOf(s) : '—';

  const picked = lines.filter((l) => l.stock);
  const hasRows = picked.length > 0;
  const sameStorage = !!sourceID && sourceID === destID;

  const wizard = useWizard<FieldError>({
    total: STEPS.length,
    check: (s) =>
      s === 1
        ? checkInfo(sourceID, destID)
        : s === 2
          ? checkLines(lines)
          : [],
  });
  const { step } = wizard;

  const next = () => setErrors(wizard.next());
  const back = () => {
    setErrors([]);
    wizard.back();
  };

  const applySource = (id: string) => {
    setSourceID(id);
    // The rows were stock of the old storage.
    setLines([newLine()]);
    setErrors([]);
  };
  const pickSource = (id: string, storage: WarehouseStorage) => {
    if (id === sourceID) return;
    if (hasRows) setPendingSource(storage);
    else applySource(id);
  };

  const editLine = (key: number, patch: Partial<DraftLine>) => {
    setLines((prev) =>
      prev.map((l) => (l.key === key ? { ...l, ...patch } : l))
    );
    setErrors([]);
  };
  const removeLine = (key: number) => {
    setLines((prev) => {
      const rest = prev.filter((l) => l.key !== key);
      return rest.length ? rest : [newLine()];
    });
    setErrors([]);
  };

  const submit = async () => {
    const problems = wizard.checkAll();
    if (problems.length) {
      setErrors(problems);
      return;
    }
    setBusy(true);
    setErrors([]);
    try {
      const created = await warehouseApi.createTransfer(
        toPayload({
          hubID,
          hubName,
          sourceStorageID: sourceID,
          destStorageID: destID,
          notes,
          lines: picked,
        })
      );
      toast.success('Transfer diajukan', { description: created.code });
      navigate(`${WAREHOUSE_PATHS.storageTransfer}/${created.id}`);
    } catch (err) {
      const e = parseWarehouseError(err);
      if (e.code === 'INSUFFICIENT_STOCK') {
        setShortfall({ rows: e.shortfall, message: e.message });
        return;
      }
      // 422 field errors → inline at their field / row, back to that step.
      const shown = server.fromError(err, TRANSFER_CODE_FIELD);
      if (shown.step !== null) wizard.goTo(shown.step);
      else setErrors([{ field: '', message: shown.message }]);
    } finally {
      setBusy(false);
    }
  };

  const errorAlert = errors.length > 0 && (
    <Alert
      status="error"
      appearance="stroke"
      size="lg"
      title={`${errors.length} isian perlu diperbaiki`}
      data-testid="transfer-errors"
    >
      <ul className="list-disc pl-4">
        {errors.map((e, i) => (
          <li key={`${i}-${e.message}`}>{e.message}</li>
        ))}
      </ul>
    </Alert>
  );

  return (
    <div className="flex flex-col gap-5">
      <WizardSteps steps={STEPS} current={step} />
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <div className="flex flex-col gap-5 xl:col-span-2">
          {step === 1 && (
            <Section title="Informasi">
              <div className="flex flex-col gap-3">
                {errorAlert}
                <FormField label="Hub" htmlFor="transfer-hub">
                  <TextInput
                    id="transfer-hub"
                    size="md"
                    value={hubName ?? `Hub #${hubID}`}
                    onChange={() => undefined}
                    disabled
                  />
                </FormField>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <FormField
                    label="Storage asal"
                    required
                    error={fe('source')}
                    hint="Storage aktif tempat stok diambil: location, section, atau bin."
                  >
                    <StorageSelect
                      testId="transfer-source"
                      hubID={hubID}
                      value={sourceID}
                      onChange={pickSource}
                      labels={labels}
                      placeholder="Pilih storage asal"
                      error={!!fe('source')}
                    />
                  </FormField>
                  <FormField
                    label="Storage tujuan"
                    required
                    error={sameStorage ? SAME_STORAGE : fe('dest')}
                    hint="Storage aktif tujuan, level apa pun, selain storage asal."
                  >
                    <StorageSelect
                      testId="transfer-dest"
                      hubID={hubID}
                      value={destID}
                      excludeIds={sourceID ? [sourceID] : []}
                      onChange={(id) => {
                        setDestID(id);
                        setErrors([]);
                      }}
                      labels={labels}
                      placeholder="Pilih storage tujuan"
                      error={sameStorage || !!fe('dest')}
                    />
                  </FormField>
                </div>
                <FormField
                  label="Alasan transfer"
                  htmlFor="transfer-notes"
                  optional
                  error={fe('notes')}
                >
                  <TextArea
                    id="transfer-notes"
                    rows={3}
                    value={notes}
                    onChange={setNotes}
                    placeholder="Mis. penataan ulang rak, pindah ke area karantina"
                  />
                </FormField>
              </div>
            </Section>
          )}

          {step === 2 && (
            <Section
              title="Daftar produk"
              description={`Stok dari ${storageText(sourceStorage)}`}
            >
              <div className="flex flex-col gap-2" data-testid="transfer-rows">
                {errorAlert}
                <div
                  className={`${LINE_GRID} text-[11px] font-medium text-text-soft-400`}
                >
                  <span>Produk &amp; batch</span>
                  <span className="text-right">Qty</span>
                  <span>UoM</span>
                  <span />
                </div>
                {lines.map((l, i) => {
                  const n = i + 1;
                  const others = new Set(
                    lines
                      .filter((x) => x.key !== l.key && x.stock)
                      .map((x) => (x.stock as { id: string }).id)
                  );
                  const rowError = lineError(i) ?? qtyIssue(l);
                  const stockErr = !l.stock && !!lineError(i);
                  return (
                    <div
                      key={l.key}
                      className="flex flex-col gap-1"
                      data-testid={`transfer-line-${i}`}
                    >
                      <div className={LINE_GRID}>
                        <StockBatchSelect
                          aria-label={`Produk & batch baris ${n}`}
                          testId={`transfer-batch-${i}`}
                          hubID={hubID}
                          storageID={sourceID}
                          value={l.stock?.id ?? ''}
                          selectedLabel={
                            l.stock
                              ? `${l.stock.sku} · ${l.stock.batchNumber} · ${STOCK_TYPE_META[l.stock.stockType].label}`
                              : undefined
                          }
                          excludeIds={others.size ? others : NO_STOCK_IDS}
                          invalid={stockErr}
                          onPick={(stock) =>
                            editLine(l.key, { stock, qty: '' })
                          }
                        />
                        <TextInput
                          aria-label={`Qty baris ${n}`}
                          size="md"
                          inputMode="decimal"
                          value={l.qty}
                          disabled={!l.stock}
                          invalid={!!rowError && !stockErr}
                          placeholder="0"
                          onChange={(v) => editLine(l.key, { qty: v })}
                          className="text-right"
                        />
                        <span className="flex h-9 items-center text-[12px] text-text-sub-600">
                          {l.stock?.uomName ?? ''}
                        </span>
                        <div className="flex h-9 items-center justify-end">
                          <Button
                            tone="neutral"
                            style="ghost"
                            size="icon-sm"
                            title="Hapus baris"
                            aria-label={`Hapus baris ${n}`}
                            disabled={lines.length === 1 && !l.stock}
                            onClick={() => removeLine(l.key)}
                          >
                            <i className="ri-delete-bin-line text-[14px]" />
                          </Button>
                        </div>
                      </div>
                      {l.stock && (
                        <div className="flex items-center gap-2">
                          <StockTypeBadge type={l.stock.stockType} />
                          <p className={metaCls}>
                            {l.stock.itemName} · tersedia{' '}
                            {fmtQty(l.stock.qtyAvailable)} {l.stock.uomName}
                          </p>
                        </div>
                      )}
                      {rowError && <InlineError>{rowError}</InlineError>}
                    </div>
                  );
                })}
                <div>
                  <Button
                    tone="neutral"
                    style="ghost"
                    size="xs"
                    data-testid="transfer-add-line"
                    onClick={() => setLines((p) => [...p, newLine()])}
                    leftIcon={<i className="ri-add-line text-[14px]" />}
                  >
                    Tambah produk
                  </Button>
                </div>
                {fe('lines') && <InlineError>{fe('lines')}</InlineError>}
              </div>
            </Section>
          )}

          {step === 3 && (
            <Section title="Review">
              <div className="flex flex-col gap-4" data-testid="transfer-review">
                {errorAlert}
                <KVGrid
                  items={[
                    ['Hub', hubName ?? `Hub #${hubID}`],
                    ['Storage asal', storageText(sourceStorage)],
                    ['Storage tujuan', storageText(destStorage)],
                    ['Alasan transfer', notes.trim() || null],
                  ]}
                />
                <TableData size="sm" data={reviewLines(picked)} columns={REVIEW_COLUMNS} />
                <Alert
                  status="information"
                  appearance="stroke"
                  size="lg"
                  title="Stok dicadangkan setelah pengajuan"
                >
                  Qty di atas dicadangkan di storage asal begitu transfer
                  diajukan, lalu dipindahkan ke storage tujuan saat transfer
                  diselesaikan.
                </Alert>
              </div>
            </Section>
          )}

          <WizardFooter
            step={step}
            total={STEPS.length}
            onBack={back}
            onNext={next}
            onSubmit={() => void submit()}
            submitLabel="Ajukan transfer"
            submitTestId="transfer-submit"
            busy={busy}
            onCancel={() => navigate(WAREHOUSE_PATHS.storageTransfer)}
          />
        </div>
        <div className="flex flex-col gap-5">
          <Section title="Ringkasan">
            <KV label="Hub" value={hubName ?? `Hub #${hubID}`} />
            <KV label="Asal" value={sourceID ? storageText(sourceStorage) : null} />
            <KV label="Tujuan" value={destID ? storageText(destStorage) : null} />
            <KV label="Produk" value={hasRows ? String(picked.length) : null} />
            <KV
              label="Total qty"
              value={hasRows ? fmtQty(totalQty(picked)) : null}
            />
          </Section>
        </div>
      </div>

      <ActionDialog
        open={!!pendingSource}
        title="Ganti storage asal"
        subtitle={pendingSource ? storagePathOf(pendingSource) : undefined}
        description="Stok dipilih per storage asal, jadi daftar produk yang sudah diisi akan dikosongkan."
        confirmLabel="Ganti & kosongkan"
        tone="destructive"
        testId="transfer-source-dialog"
        onConfirm={() => {
          if (pendingSource) applySource(pendingSource.id);
          setPendingSource(null);
        }}
        onClose={() => setPendingSource(null)}
      />
      <ShortfallModal
        open={!!shortfall}
        orderCode="Transfer storage"
        subtitle="Transfer tidak diajukan, belum ada stok yang dicadangkan"
        rows={shortfall?.rows ?? []}
        message={shortfall?.message}
        onClose={() => {
          setShortfall(null);
          wizard.goTo(2);
        }}
      />
    </div>
  );
};

/* --------------------------------- page ---------------------------------- */

const StorageTransferNewPage: React.FC = () => {
  const { hubID, hub, setHubID } = useWarehouse();
  return (
    <div className="space-y-5 p-6">
      <DetailHeader
        backTo={WAREHOUSE_PATHS.storageTransfer}
        backLabel="Transfer storage"
        title="Ajukan transfer storage"
        actions={
          hubID === null ? (
            <HubSelect
              value={hubID}
              onChange={setHubID}
              className="w-48"
              aria-label="Hub"
            />
          ) : undefined
        }
      />
      {hubID === null ? (
        <HubRequired />
      ) : (
        // A new hub starts the form over: its bins are not this hub's.
        <TransferForm key={hubID} hubID={hubID} hubName={hub?.name} />
      )}
    </div>
  );
};

export default StorageTransferNewPage;
